import {
  Fragment,
  useState,
  useEffect,
  useRef,
  type Key,
  type ReactNode,
  type SetStateAction,
  type Dispatch,
  type RefObject,
} from 'react';
import * as React from 'react';
import { Item } from 'react-stately';
import { useDebounce, useMedia } from 'react-use';
import { useExperiment } from '@hungryroot/experiments';
import { CaretDownNew } from '@hungryroot/icons';
import { log } from '@hungryroot/o11y';
import {
  Heading,
  Modal,
  Select,
  TextArea,
  TextInput,
  Checkbox,
} from '@hungryroot/ramen';
import { type Customer } from '@hungryroot/rest';
import { BreakpointQueryLarge } from '@hungryroot/tokens';
import {
  suggestionHasSecondaryEntries,
  type SuggestionHasSecondaryEntries,
  type Suggestion,
} from '../../address/main';
import {
  usePatchCustomerAddress,
  useSmartyInitialQuery,
  useSmartySecondaryQuery,
} from '../../hooks';
import { Accordion } from '../Accordion/Accordion';
import { AddressDropdown } from '../AddressDropdown/AddressDropdown';
import { states } from './states';
import {
  isPhoneError,
  validateRequiredFields,
  PHONE_MAX_LENGTH,
  getAddressSuggestions,
  initialAddressSuggestion,
  ADDRESS_PLACEHOLDER,
  getAddressRequestBody,
  getAddressErrorMessage,
  type Address,
  type AddressDetails,
  type ErrorFields,
} from './utils';
import styles from './AddressForm.module.css';

interface Props {
  testid?: string;
  customer: Customer;
  displayAddress?: Address;
  displayAddressDetails?: AddressDetails;
  addressState?: Address;
  addressDetailsState?: AddressDetails;
  errorMessage?: ErrorFields;
  setAddressState: Dispatch<SetStateAction<Address | undefined>>;
  setAddressDetailsState: Dispatch<SetStateAction<AddressDetails | undefined>>;
  setErrorMessage: Dispatch<SetStateAction<ErrorFields | undefined>>;
  containerRef: RefObject<HTMLDivElement>;
}

function strHasValue(str?: string) {
  if (typeof str === 'string' && str.length > 0) {
    return true;
  }
  return;
}

export function AddressForm(props: Props) {
  const {
    testid,
    customer,
    addressState,
    setAddressState,
    addressDetailsState,
    setAddressDetailsState,
    errorMessage,
    setErrorMessage,
    displayAddress,
    displayAddressDetails,
    containerRef,
  } = props;
  const isDesktop = useMedia(BreakpointQueryLarge, false);
  const exp159 = useExperiment(159);
  const inExp159Variant = exp159 !== undefined && exp159 > 0;
  const exp164 = useExperiment(164);
  const inExp164Variant = exp164 !== undefined && exp164 > 0;

  const showFlexCreditMessage = !inExp159Variant && inExp164Variant;

  const { id: customerId, first_name, last_name } = customer;
  const customerName =
    strHasValue(first_name) &&
    strHasValue(last_name) &&
    `${first_name} ${last_name}`;

  const persistCustomerAddress = usePatchCustomerAddress();

  const [addressInput, setAddressInput] = useState<string>('');
  const [addressInputIsFocused, setAddressInputIsFocused] = useState(false);
  const [secondaryQuery, setSecondaryQuery] = useState<
    SuggestionHasSecondaryEntries | undefined
  >();
  const [inputTextValue, setInputTextValue] = useState<string>();

  const [phoneInputIsFocused, setPhoneInputIsFocused] = useState(false);

  const [firstError, setFirstError] = useState<null | string>(null);

  const scrollRef = useRef<null | HTMLDivElement>(null);
  useEffect(() => {
    if (errorMessage) {
      setFirstError(Object.keys(errorMessage)[0]);
    }
    if (scrollRef?.current) {
      // on Desktop, scroll the content container - header height,
      // otherwise scroll the whole page
      if (isDesktop && containerRef?.current) {
        const heightFromTop = scrollRef.current.offsetTop - 90;
        containerRef.current.scrollTo({
          top: heightFromTop,
          behavior: 'smooth',
        });
      } else {
        scrollRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [errorMessage, firstError, scrollRef]);

  const [debouncedValue, setDebouncedValue] = React.useState('');
  const [, cancel] = useDebounce(
    () => {
      if (secondaryQuery !== undefined) {
        return;
      }
      setDebouncedValue(addressInput);
    },
    500,
    [addressInput]
  );

  const { data: smartyPrimaryQuery } = useSmartyInitialQuery(debouncedValue, {
    enabled: debouncedValue !== undefined && !!debouncedValue.length,
  });
  const { data: smartySecondaryQuery } = useSmartySecondaryQuery(
    debouncedValue,
    secondaryQuery,
    { enabled: debouncedValue !== undefined && secondaryQuery !== undefined }
  );

  const addressSuggestionsArr = getAddressSuggestions(
    smartyPrimaryQuery,
    smartySecondaryQuery
  );

  const addressSuggestions = addressSuggestionsArr.reduce(
    (acc, suggestion) => {
      const secondaryQueryInputText = `${suggestion.streetLine} ${suggestion.secondary}`;
      const { hasSecondaryEntries } = suggestionHasSecondaryEntries(suggestion);
      const link = hasSecondaryEntries ? (
        <span className={styles['button-more']}>
          <Heading variant="H6" className={styles['button-more-text']}>
            {`+${suggestion.entries} addresses`}
            <CaretDownNew />
          </Heading>
        </span>
      ) : null;
      const secondaryText = hasSecondaryEntries
        ? `${suggestion.secondary}...`
        : suggestion.secondary;
      const key = `${suggestion.streetLine} ${secondaryText} ${suggestion.city} ${suggestion.state}`;

      acc[key] = {
        key,
        suggestion,
        link,
        secondaryQueryInputText,
      };
      return acc;
    },
    {} as Record<
      string,
      {
        suggestion: Suggestion;
        key: string;
        link: ReactNode | null;
        secondaryQueryInputText: string;
      }
    >
  );

  const manualEntryText = "Don't see your address?";
  addressSuggestions[manualEntryText] = {
    key: manualEntryText,
    suggestion: initialAddressSuggestion,
    link: (
      <span className={styles['button-more']}>
        <Heading variant="H6">Enter address manually</Heading>
      </span>
    ),
    secondaryQueryInputText: manualEntryText,
  };

  function onSelectionChange(key: Exclude<Key, bigint>) {
    // Clear inline error message focus so focus returns to the correct input if error persists
    setFirstError(null);

    if (!addressInput.length) {
      return setSecondaryQuery(undefined);
    }

    const { suggestion: selected } = addressSuggestions[key];
    const secondarySuggestion = suggestionHasSecondaryEntries(selected);

    if (!secondarySuggestion.hasSecondaryEntries) {
      if (key === manualEntryText) {
        setAddressInputIsFocused(true);
        return setAddressState({
          street: addressInput,
          apt: undefined,
          city: undefined,
          state: undefined,
          zipcode: undefined,
        });
      }

      const selectedAddress = {
        street: selected.streetLine,
        apt: selected.secondary,
        city: selected.city,
        state: selected.state,
        zipcode: selected.zipcode,
      };
      const addressObj = {
        ...selectedAddress,
        name: addressDetailsState?.name ?? customerName,
        sms_opt_in: addressDetailsState?.sms_opt_in ?? true,
      };

      const requiredFieldsError = validateRequiredFields(addressObj);
      if (requiredFieldsError !== null) {
        setErrorMessage({ ...requiredFieldsError });
      } else {
        persistCustomerAddress.mutate(
          {
            address: getAddressRequestBody(addressObj),
          },
          {
            onSuccess() {
              const data = {
                customerId,
                address: addressObj,
              };
              log('checkout_smarty_address_selected', data);
            },
            onError(e) {
              const { heading, body } = getAddressErrorMessage(e);
              const data = {
                customerId,
                address: addressObj,
                error: e,
              };
              log('checkout_smarty_address_selected_error', data);

              setErrorMessage({
                ...errorMessage,
                modal: {
                  heading,
                  body,
                },
              });
            },
          }
        );
        setPhoneInputIsFocused(true);
      }
    } else {
      setInputTextValue(`${selected.streetLine} ${selected.secondary}`);
      setSecondaryQuery(secondarySuggestion);
    }
  }

  if (exp159 === undefined) {
    return null;
  }

  return (
    <div ref={containerRef}>
      <Heading
        variant={inExp159Variant ? 'H3' : 'H4'}
        className={styles.heading}
      >
        Delivery address
      </Heading>
      <form data-testid={testid} className={styles.form}>
        <div className={styles.row}>
          <TextInput
            label="Full Name"
            errorMessage={errorMessage?.name}
            className={styles.item}
            name="name"
            value={displayAddressDetails?.name ?? customerName}
            onChange={(value) => {
              setAddressDetailsState({ ...displayAddressDetails, name: value });
            }}
            textInputRef={firstError === 'name' ? scrollRef : undefined}
            isFocused={firstError === 'name'}
          />
        </div>
        {displayAddress === undefined && (
          <div className={styles.row}>
            <AddressDropdown
              testid={`${testid}-dropdown`}
              placeholder={ADDRESS_PLACEHOLDER}
              label="Address"
              onFocusChange={(isFocused) => {
                if (!isFocused) {
                  setAddressInput('');
                  cancel();
                }
              }}
              onInputChange={(value) => {
                if (value.trim() !== inputTextValue) {
                  setSecondaryQuery(undefined);
                }
                setAddressInput(value);
              }}
              onSelectionChange={onSelectionChange}
              items={Object.values(addressSuggestions)}
              className={styles.item}
              open={!!secondaryQuery}
              name="address1"
            >
              {({ key, link, secondaryQueryInputText }) => {
                return (
                  <Item key={key} textValue={secondaryQueryInputText}>
                    {key}
                    {link}
                  </Item>
                );
              }}
            </AddressDropdown>
          </div>
        )}

        {displayAddress !== undefined && (
          <Fragment key="form-body">
            <div className={styles.row}>
              <TextInput
                isFocused={addressInputIsFocused || firstError === 'street'}
                label="Address"
                placeholder={ADDRESS_PLACEHOLDER}
                value={displayAddress.street}
                className={styles.item}
                errorMessage={errorMessage?.street}
                onChange={(value) => {
                  setAddressState({ ...displayAddress, street: value });
                }}
                name="address1"
                textInputRef={firstError === 'street' ? scrollRef : undefined}
              />
            </div>
            <div className={styles.row}>
              <TextInput
                label="Apt number / suite (optional)"
                value={displayAddress.apt || undefined}
                className={styles.item}
                onChange={(value) => {
                  setAddressState({ ...displayAddress, apt: value });
                }}
                name="address2"
              />
            </div>
            <div className={styles.row}>
              <TextInput
                label="City"
                value={displayAddress.city}
                className={styles['full-width-item']}
                errorMessage={errorMessage?.city}
                onChange={(value) => {
                  setAddressState({ ...displayAddress, city: value });
                }}
                name="city"
                isFocused={firstError === 'city'}
              />
              <Select
                testid={`${testid}-select`}
                label="State"
                items={states}
                className={styles.item}
                errorMessage={errorMessage?.state}
                selectedKey={displayAddress.state}
                onSelectionChange={(key) => {
                  setAddressState({ ...displayAddress, state: key as string });
                }}
                name="state"
              >
                {(item) => (
                  <Item key={item.abbreviation}>{item.abbreviation}</Item>
                )}
              </Select>
              <TextInput
                label="Zip code"
                value={displayAddress.zipcode}
                className={styles.item}
                maxLength={5}
                errorMessage={errorMessage?.zipcode}
                pattern="[0-9]*"
                type="tel"
                isZipcode={true}
                onChange={(value: string) => {
                  setAddressState({ ...displayAddress, zipcode: value });
                }}
                isFocused={firstError === 'zipcode'}
                textInputRef={firstError === 'zipcode' ? scrollRef : undefined}
                onBlur={() => {
                  if (displayAddress === undefined) {
                    return;
                  }

                  const addressObj = {
                    ...displayAddress,
                    name: displayAddressDetails?.name ?? customerName,
                    sms_opt_in: displayAddressDetails?.sms_opt_in ?? true,
                  };

                  const requiredFieldsError =
                    validateRequiredFields(addressObj);
                  if (requiredFieldsError !== null) {
                    setErrorMessage({ ...requiredFieldsError });
                  } else {
                    setErrorMessage(undefined);
                    persistCustomerAddress.mutate(
                      {
                        address: getAddressRequestBody(addressObj),
                      },
                      {
                        onError(e) {
                          const { heading, body } = getAddressErrorMessage(e);

                          setErrorMessage({
                            ...errorMessage,
                            modal: {
                              heading,
                              body,
                            },
                          });
                        },
                      }
                    );
                  }
                }}
                name="zip_code"
              />
            </div>
          </Fragment>
        )}
        <div className={styles.row}>
          <Accordion title="Delivery Instructions" className={styles.item}>
            <TextArea
              placeholder="Add details like building description, leave with the doorman, etc"
              aria-label="delivery-instructions"
              defaultValue={displayAddressDetails?.note || undefined}
              maxLength={100}
              rows={2}
              onChange={(value) => {
                setAddressDetailsState({
                  ...displayAddressDetails,
                  note: value,
                });
              }}
            />
          </Accordion>
        </div>
        <div className={styles.row}>
          <TextInput
            type="tel"
            label="Phone number"
            value={displayAddressDetails?.phone || undefined}
            maxLength={PHONE_MAX_LENGTH}
            errorMessage={errorMessage?.phone}
            className={styles.item}
            isFocused={phoneInputIsFocused || firstError === 'phone'}
            textInputRef={firstError === 'phone' ? scrollRef : undefined}
            onChange={(value) => {
              setAddressDetailsState({
                ...displayAddressDetails,
                phone: value,
              });

              if (
                addressDetailsState?.phone?.length === PHONE_MAX_LENGTH &&
                value.length < PHONE_MAX_LENGTH
              ) {
                const err = isPhoneError(value);
                setErrorMessage({ ...errorMessage, phone: err });
              }
            }}
            validateOnBlur={async (value) => {
              if (errorMessage?.phone) {
                const err = isPhoneError(value);
                setErrorMessage({ ...errorMessage, phone: err });
              }
            }}
          />
        </div>
        <div className={styles.row}>
          <Checkbox
            testid={testid}
            label={`Get delivery notifications and text messages from us*${
              showFlexCreditMessage ? '*' : ''
            }`}
            value="true"
            isChecked={displayAddressDetails?.sms_opt_in ?? true}
            className={styles.sms}
            onChange={() => {
              setAddressDetailsState({
                ...displayAddressDetails,
                sms_opt_in: displayAddressDetails
                  ? !displayAddressDetails.sms_opt_in
                  : false,
              });
            }}
          />
        </div>
      </form>
      <Modal
        isOpen={!!errorMessage?.modal}
        onActionClick={() => {
          setAddressState({
            ...addressState,
            street: '',
            apt: '',
            city: '',
            state: '',
            zipcode: '',
          });
          setErrorMessage(undefined);
        }}
        onDismiss={() => setErrorMessage(undefined)}
        confirmButtonText="Enter a new address"
        secondaryButtonText="Cancel"
        ariaLabel="error-modal"
        heading={errorMessage?.modal?.heading}
        body={errorMessage?.modal?.body}
      />
    </div>
  );
}
